import { Typography } from '@mui/material';
import moment from 'moment';
import React from "react";
import {
  ArrayField,
  AutocompleteArrayInput,
  AutocompleteInput,
  ChipField,
  Datagrid,
  DateField,
  FormDataConsumer,
  FunctionField,
  NumberField,
  ReferenceArrayField,
  ReferenceArrayInput,
  ReferenceField,
  ReferenceInput,
  SelectArrayInput,
  SelectInput,
  SingleFieldList,
  TextField,
  useListContext,
  usePermissions,
  useTranslate,
} from 'react-admin';
import { ReportList } from '../../components';
import { BetweenDatesInput } from '../../components/BetweenDatesInput';
import ListPagination from '../../components/ListPagination';
import Permission from '../../components/Permission';
import { PERMISSIONS } from "../../constants";
import { __t } from '../../i18n/t';
import { getChainId, getPlaceId } from "../../lib";
import { FillinOrigin, TagType } from "../../models";
import { formatAttendantLabel, formatNumber, filterToQuery } from "../../utils";

// Mesmas opções do relatório de abastecimentos. Valor de constante não é embrulhado pelo plugin de
// i18n nem visto pelo extrator: as entradas em espanhol estão à mão em i18n/messages/es/catalog/base.ts.
const discountChoices = [
  { id: 'withDiscount', name: 'Com Desconto' },
  { id: 'withoutDiscount', name: 'Sem Desconto' }
];

const titleSx = {
  margin: '10px 0 10px 0',
  textAlign: 'center',
  fontWeight: 'bold',
};

const refArrayInputSx = {
  minWidth: 180,
  '& .MuiFormHelperText-marginDense': {
    height: 0
  }
};

const FillinByAttendant = () => {
  const props = useListContext();

  let data = [];
  let summaryData = [];

  if (props.data) {
    const reportInfo = props.data[0];

    if (reportInfo) {
      data = reportInfo.data;
      summaryData = [reportInfo.summaryData];
    }
  }

  return (
    <>
      <Typography sx={titleSx}>Total</Typography>
      <Datagrid bulkActionButtons={false} style={{ marginBottom: 60 }} data={summaryData} total={1}>
        <NumberField
          source="total_value"
          label="Total em Valor"
          textAlign="center"
          options={{
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }}
          sortable={false}
        />
        <TextField
          source="total_fillins"
          textAlign="center"
          label="Abastecimentos no App"
          sortable={false}
        />
        <TextField
          source="outsideTheApp"
          textAlign="center"
          label="Abastecimentos fora do App"
          sortable={false}
        />
        <FunctionField
          textAlign="center"
          label="Total de abastecimentos"
          sortable={false}
          render={record => +record.total_fillins + +(record.outsideTheApp || 0)}
        />
        <NumberField
          source="total_amount"
          label="Total em Litros"
          textAlign="center"
          options={{
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }}
          sortable={false}
        />
        <NumberField
          source="average_ticket"
          label="Ticket Médio"
          textAlign="center"
          options={{
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }}
          sortable={false}
        />
        <NumberField
          source="average_amount"
          label="Volume Médio"
          textAlign="center"
          options={{
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }}
          sortable={false}
        />
        <NumberField
          source="consider_filling_completely"
          label="Encheu Tanque"
          textAlign="center"
          options={{
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }}
          sortable={false}
        />
        <FunctionField
          source="avg_attendantrating"
          label="Nota Média"
          textAlign="center"
          emptyText="0"
          render={record => formatNumber(record.avg_attendantrating, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          sortable={false}
        />
        <NumberField
          source="count_attendantrating"
          label="Total de Avaliações"
          textAlign="center"
          emptyText="0"
          sortable={false}
        />
      </Datagrid>
      <Typography sx={titleSx}>Frentistas</Typography>
      <Datagrid bulkActionButtons={false} style={{ marginTop: 40 }} data={data} total={1} resource={props.resource}>
        <TextField
          source="attendant_name"
          textAlign="center"
          label="Frentista"
        />
        <TextField
          source="identfid"
          textAlign="center"
          label="Identfid"
        />
        <NumberField
          source="total_value"
          label="Total em Valor"
          textAlign="center"
          options={{
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }}
        />
        <TextField
          source="total_fillins"
          textAlign="center"
          label="Abastecimentos no App"
        />
        <TextField
          source="outsideTheApp"
          textAlign="center"
          label="Abastecimentos fora do App"
          sortable={false}
        />
        <FunctionField
          textAlign="center"
          label="Total de abastecimentos"
          sortable={false}
          render={record => +record.total_fillins + +(record.outsideTheApp || 0)}
        />
        <NumberField
          source="total_amount"
          label="Total em Litros"
          textAlign="center"
          options={{
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }}
        />
        <NumberField
          source="average_ticket"
          label="Ticket Médio"
          textAlign="center"
          options={{
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }}
        />
        <NumberField
          source="average_amount"
          label="Volume Médio"
          textAlign="center"
          options={{
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }}
        />
        <NumberField
          source="consider_filling_completely"
          label="Encheu Tanque"
          textAlign="center"
          options={{
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }}
        />
        <FunctionField
          source="avg_attendantrating"
          label="Nota Média"
          textAlign="center"
          emptyText="0"
          render={record => formatNumber(record.avg_attendantrating, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        />
        <NumberField
          source="count_attendantrating"
          label="Total de Avaliações"
          textAlign="center"
          emptyText="0"
        />
      </Datagrid>
    </>
  );
};

const fillinByAttendantExporter = async (data) => {
  let attendantInfo = data[0].data;
  return attendantInfo.map(attendant => {
    return {
      frentista: attendant.attendant_name || 'Sem nome',
      identfid: attendant.identfid,
      total_em_valor: attendant.total_value,
      total_de_abastecimentos: attendant.total_fillins,
      total_em_litros: attendant.total_amount,
      ticket_medio: attendant.average_ticket,
      volume_medio: attendant.average_amount,
      encheu_tanque: attendant.consider_filling_completely,
      nota_media: attendant.avg_attendantrating ? Number.parseFloat(attendant.avg_attendantrating).toFixed(2) : 'Sem Nota',
      total_de_avaliacoes: attendant.count_attendantrating || "0"
    };
  });
};


const FilterTablePrint = () => {
  const { filterValues } = useListContext();
  const { permissions } = usePermissions();
  return (
    <Datagrid bulkActionButtons={false} data={[{ ...filterValues, generatedReportAt: moment().toISOString() }]} total={1}>
      <DateField source="from" textAlign="center" label="De" sortable={false} locales="pt-BR" />
      <DateField source="to" textAlign="center" label="Até" sortable={false} locales="pt-BR" />
      <ReferenceField label="Frentista" emptyText="Todos" source="attendantId" textAlign="center" basePath="attendants" reference="attendants" link={false}>
        <FunctionField render={record => formatAttendantLabel(record)} />
      </ReferenceField>
      <ReferenceField label="Posto" emptyText="Todos" source="stationId" textAlign="center" basePath="stations" reference="stations" link={false}>
        <TextField source="name" />
      </ReferenceField>
      {permissions && permissions.includes(PERMISSIONS.TAG) &&
        <ReferenceArrayField label="Etiquetas" emptyText="Todas" source="tagIds" textAlign="center" reference='chains/chainId/tag'>
          <SingleFieldList>
            <ChipField source='name' />
          </SingleFieldList>
        </ReferenceArrayField>
      }
      <ArrayField label="Origem" emptyText="Todas" source="origin" textAlign="center">
        <SingleFieldList>
          <FunctionField render={(record) => (
            <ChipField record={{ name: FillinOrigin.translateOrigin(record) }} source="name" />
          )} />
        </SingleFieldList>
      </ArrayField>
      <FunctionField
        label="Usou desconto"
        emptyText="Todos"
        textAlign="center"
        render={record => {
          const choice = discountChoices.find(({ id }) => id === record.discount);
          return choice && __t(choice.name);
        }}
      />
      <ReferenceArrayField label="Desconto" emptyText="Todos" source="discountIds" textAlign="center" reference="discounts" link={false}>
        <SingleFieldList>
          <ChipField source="identify" />
        </SingleFieldList>
      </ReferenceArrayField>
      <DateField source="generatedReportAt" textAlign="center" label="Gerado às" showTime sortable={false} locales="pt-BR" />
    </Datagrid>
  );
};

const ReportFillinByAttendant = (props) => {
  const placeId = getPlaceId();
  const translate = useTranslate();

  const listFilters = [
    <BetweenDatesInput alwaysOn />,
    <ReferenceInput
      source="attendantId"
      reference="attendants"
      sort={{ field: "name", order: "ASC" }}
      perPage={99999}
      alwaysOn
    >
      <SelectInput
        label="Frentista"
        emptyText="Todos"
        style={{ minWidth: 180 }}
        optionText={record => formatAttendantLabel(record)}
      />
    </ReferenceInput>,
    <FormDataConsumer alwaysOn>
      {({ formData }) => !placeId && (
        <ReferenceInput
          source="stationId"
          reference="stations"
          perPage={null}
          sort={{ field: "name", order: "ASC" }}
        >
          <AutocompleteInput
            filterToQuery={filterToQuery}
            label="Posto"
            disabled={formData.tagIds}
            sx={{ minWidth: 180 }}
            optionText={'name'}
            noOptionsText={translate('ra.input.autocomplete.no_options_text')}
          />
        </ReferenceInput>
      )}
    </FormDataConsumer>,
    <ReferenceArrayInput
      source="fuelIds"
      reference="fuels"
      sort={{ field: "name", order: "ASC" }}
      alwaysOn
    >
      <SelectArrayInput
        emptyText="Todos"
        label="Produto"
        style={{ minWidth: 180 }}
        optionText={'name'}
      />
    </ReferenceArrayInput>,
    <Permission permission={PERMISSIONS.TAG} alwaysOn>
      <FormDataConsumer>
        {({ formData }) => !placeId && (
          <ReferenceArrayInput
            sx={refArrayInputSx}
            source="tagIds"
            reference="chains/chainId/tag"
            sort={{ field: "name", order: "ASC" }}
            filter={{ type: TagType.station }}
          >
            <SelectArrayInput
              label="Etiqueta"
              disabled={formData.stationId}
              optionText={'name'}
            />
          </ReferenceArrayInput>
        )}
      </FormDataConsumer>
    </Permission>,
    <SelectArrayInput
      label="Origem"
      source="origin"
      choices={FillinOrigin.fillinOriginChoices}
      emptyText="Todos"
      style={{ minWidth: 160 }}
      alwaysOn
    />,
    <SelectInput
      label="Usou desconto"
      source="discount"
      choices={discountChoices}
      emptyText="Todos"
      style={{ minWidth: 160 }}
      alwaysOn
    />,
    <ReferenceArrayInput
      source="discountIds"
      reference="discounts"
      // Campanha mais nova primeiro: é a que o gestor acabou de criar e quer filtrar.
      sort={{ field: "createdAt", order: "DESC" }}
      filter={{ chainId: getChainId() }}
      perPage={100}
      alwaysOn
    >
      <AutocompleteArrayInput
        label="Desconto"
        optionText={record => record ? [record.identify, record.description].filter(Boolean).join(' - ') : ''}
        sx={{ minWidth: 250 }}
        filterToQuery={filterToQuery}
        noOptionsText={translate('ra.input.autocomplete.no_options_text')}
      />
    </ReferenceArrayInput>
  ];

  return (
    <Permission permission={PERMISSIONS.FILLIN_BY_ATTENDANT_REPORT}>
      <ReportList periodRequired
        {...props}
        basePath="fillin-by-attendant"
        title="Abastecimentos por Frentista"
        resource="chains/chainId/reports/fillin-by-attendant"
        filters={listFilters}
        sort={{ field: 'attendant_name', order: 'ASC' }}
        perPage={25}
        pagination={<ListPagination />}
        filterDefaultValues={{
          stationId: placeId,
          from: moment().startOf('day').toISOString(),
          to: moment().endOf('day').toISOString()
        }}
        bulkActionButtons={false}
        exporter={false}
        titleOnPrint="Relatório de Abast. por Frentista"
        filterTablePrint={<FilterTablePrint />}
        customExporter={fillinByAttendantExporter}
        landscape
      >
        <FillinByAttendant />
      </ReportList>
    </Permission >
  );
};

export default ReportFillinByAttendant;